import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** @extends {Component<{whatsapp: HTMLAnchorElement, selection: HTMLElement, guideOpen: HTMLAnchorElement, guideDialog?: HTMLDialogElement, guideBody?: HTMLElement}>} */
class BPSizeHelp extends Component {
  requiredRefs = ['whatsapp', 'selection', 'guideOpen'];
  #controller;
  #variantId = '';
  #sequence = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#variantId = this.dataset.variantId || '';
    const { signal } = this.#controller;
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.refs.guideDialog?.addEventListener('close', () => this.refs.guideOpen.focus({ preventScroll: true }), { signal });
    this.prepareMessage();
  }

  updatedCallback() {
    super.updatedCallback();
    this.prepareMessage();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#sequence++;
    if (this.refs.guideDialog?.open) this.refs.guideDialog.close();
    super.disconnectedCallback();
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || event.target.dataset.productId !== this.dataset.productId) return;
    const sequence = ++this.#sequence;
    this.#variantId = String(event.detail?.variantId || '');
    this.prepareMessage();
    event.promise?.then(({ detail }) => {
      if (!this.isConnected || sequence !== this.#sequence || !detail?.html) return;
      const incoming = detail.html.querySelector(`bp-size-help[data-block-id="${this.dataset.blockId}"]`);
      if (!incoming || incoming.dataset.productId !== this.dataset.productId) return;
      this.#variantId = incoming.dataset.variantId || '';
      this.dataset.selection = incoming.dataset.selection || '';
      this.dataset.code = incoming.dataset.code || '';
      this.prepareMessage();
    }).catch(() => {});
  };

  prepareMessage() {
    const root = this.closest('.product-details, .shopify-section');
    const picker = root?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const options = picker?.getAllSelectedOptions?.() || [];
    const selection = options.map((option) => option.value).filter(Boolean).join(' / ') || this.dataset.selection || '';
    const url = new URL(this.dataset.productUrl, location.origin);
    url.search = '';
    url.hash = '';
    if (/^\d+$/.test(this.#variantId)) url.searchParams.set('variant', this.#variantId);
    const lines = [this.dataset.greeting, this.dataset.productTitle];
    const code = this.dataset.articleCode || this.dataset.code;
    if (code) lines.push(`${this.dataset.referenceLabel}: ${code}`);
    if (selection) lines.push(`${this.dataset.selectionLabel}: ${selection}`);
    lines.push(url.href);
    const whatsapp = new URL(`https://wa.me/${this.dataset.phone}`);
    whatsapp.searchParams.set('text', lines.join('\n'));
    this.refs.whatsapp.href = whatsapp.href;
    this.refs.selection.textContent = `${this.dataset.selectionLabel}: ${selection}`;
  }

  /** @param {MouseEvent} event */
  openGuide(event) {
    const dialog = this.refs.guideDialog;
    if (!(dialog instanceof HTMLDialogElement)) return;
    event.preventDefault();
    if (!dialog.open) dialog.showModal();
    const body = this.refs.guideBody;
    if (!body) return;
    const patterns = { clothing: /ropa|clothing|clothes/i, footwear: /calzado|footwear|shoes/i, pablosky: /pablosky/i, kyoto: /kyoto/i };
    const pattern = patterns[this.dataset.guideFocus];
    const heading = pattern && Array.from(body.querySelectorAll('h2,h3')).find((element) => pattern.test(element.textContent));
    body.scrollTop = 0;
    if (heading) body.scrollTop = heading.getBoundingClientRect().top - body.getBoundingClientRect().top - 8;
  }

  closeGuide() {
    this.refs.guideDialog?.close();
  }

  /** @param {MouseEvent} event */
  closeOnBackdrop(event) {
    const dialog = this.refs.guideDialog;
    if (event.target !== dialog || !dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  }
}

if (!customElements.get('bp-size-help')) customElements.define('bp-size-help', BPSizeHelp);
